// Чистая логика экрана оформления (T6): валидация формы, сборка тела POST /api/book,
// суммы, разбор query-параметров возврата с оплаты. Без DOM — под юнит-тесты.
import { servicesTotal } from "./services.js";
import { isConnectionError } from "../api.js";

// Телефон: к цифрам, ведущую 8 меняем на 7; валиден при 10–15 цифрах (зеркало серверной проверки)
export function normalizePhone(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("8") ? `7${digits.slice(1)}` : digits;
  if (normalized.length < 10 || normalized.length > 15) return null;
  return `+${normalized}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// values — строки из полей формы + agree (boolean). Ошибки — по имени поля, русские тексты.
export function validateGuestForm(values) {
  const v = values ?? {};
  const errors = {};
  if (!String(v.firstName ?? "").trim()) errors.firstName = "Укажите имя";
  if (!String(v.lastName ?? "").trim()) errors.lastName = "Укажите фамилию";
  if (!normalizePhone(v.phone)) errors.phone = "Телефон в формате +7 (999) 123-45-67";
  const email = String(v.email ?? "").trim();
  if (!EMAIL_RE.test(email) || email.length > 200) errors.email = "Укажите корректный e-mail";
  if (String(v.comment ?? "").trim().length > 1000) errors.comment = "Комментарий — до 1000 символов";
  if (v.agree !== true) errors.agree = "Нужно согласие с правилами бронирования";
  return { ok: Object.keys(errors).length === 0, errors };
}

// Минимальная пауза перед отправкой формы (T10d): сервер принимает токен формы не раньше 4 с
// после выдачи. Отсчёт — от получения ответа /payment-options (он позже выдачи) по часам гостя,
// скью не важен; 250 мс — запас на таймер. tokenAt пуст → не ждём (сервер ответит сам).
export const FORM_MIN_DELAY_MS = 4_250;
export function formSubmitDelay(tokenAt, now, minMs = FORM_MIN_DELAY_MS) {
  if (!tokenAt) return 0;
  return Math.max(0, tokenAt + minMs - now);
}

// Ключ попытки брони (T34): сервер по нему отдаёт повтору ответ первой попытки — двойной клик,
// таймаут и обрыв связи не создают вторую бронь. Формат — как ждёт сервер: [A-Za-z0-9_-]{16,64}.
export function newAttemptKey(cryptoImpl = globalThis.crypto) {
  if (typeof cryptoImpl?.randomUUID === "function") return cryptoImpl.randomUUID();
  if (typeof cryptoImpl?.getRandomValues === "function") {
    const bytes = cryptoImpl.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  // без crypto (очень старый браузер) — уникальности хватает, секретом ключ не является
  const r = () => Math.random().toString(36).slice(2, 10).padEnd(8, "0");
  return `${Date.now().toString(36)}-${r()}-${r()}`;
}

// Отпечаток формы без служебных полей: тот же ключ — только для тех же данных брони
export function bookFingerprint(payload) {
  const rest = { ...payload };
  delete rest.formToken;
  delete rest.okb_extra;
  delete rest.attemptKey;
  return JSON.stringify(rest);
}

// Попытка для отправки: прежняя, если форма та же и исход прошлой отправки неизвестен, иначе новая
export function nextBookAttempt(prev, fingerprint, makeKey = newAttemptKey) {
  return prev && prev.fp === fingerprint ? prev : { key: makeKey(), fp: fingerprint };
}

// Оставить ключ после ошибки? Да, если ответа нашего сервера не было: обрыв или таймаут, 5xx и
// 429 без нашего code (прокси во время деплоя, лимит запросов в минуту), или исход брони неизвестен
// (code unknown — повтор отдаст тот же ответ, а не создаст вторую бронь). Наш ответ с code —
// исход известен, следующая отправка — новая попытка.
export function keepBookAttempt(err) {
  if (!err) return false;
  if (isConnectionError(err)) return true;
  const code = err.body?.code;
  if (code === "unknown") return true;
  if (code) return false;
  return err.status === 429 || err.status >= 500;
}

// Ожидание ответа брони (T35): Шелтер создаёт бронь обычно за 2–5 с, бывает 10 с и дольше (день
// запуска 09.10.2026 — 10 с), сервер ждёт putOrder до 30 с. Пока ждём, кнопка пульсирует, а текст и
// подсказка под ней меняются по времени с нажатия — чтобы гость не решил, что всё зависло.
export const BOOK_WAIT_STAGES = Object.freeze([
  { afterMs: 0, button: "Отправляем…", hint: "" },
  {
    afterMs: 5_000,
    button: "Создаём бронь…",
    hint:
      "Система бронирования отеля оформляет бронь — обычно это до 15 секунд, иногда до 30. " +
      "Пожалуйста, не закрывайте страницу.",
  },
  {
    afterMs: 20_000,
    button: "Создаём бронь…",
    hint:
      "Система отеля отвечает дольше обычного. Ещё немного — бронь не потеряется, нажимать " +
      "повторно не нужно.",
  },
]);
// автоповтор после 60 с без ответа (T34): тот же ключ, сервер отдаст исход первой попытки
export const BOOK_RETRY_STAGE = Object.freeze({
  button: "Проверяем бронь…",
  hint: "Связь прервалась — проверяем, создалась ли бронь. Повторной брони не будет.",
});
export function bookWaitStage(elapsedMs, { retrying = false } = {}) {
  if (retrying) return BOOK_RETRY_STAGE;
  let stage = BOOK_WAIT_STAGES[0];
  for (const s of BOOK_WAIT_STAGES) if (elapsedMs >= s.afterMs) stage = s;
  return stage;
}

// Повтор брони (T34): после обрыва или таймаута — ровно одна вторая попытка с ТЕМ ЖЕ телом и
// ключом (сервер присоединит её к идущей или отдаст готовый ответ). Пауза — как у «Найти» (T24).
export const BOOK_RETRY_FAILED_TEXT =
  "Нет связи с сервером бронирования. Нажмите «Забронировать» ещё раз — повторной брони не будет.";
export async function bookWithRetry(api, payload, { onRetry, retryPauseMs = 400 } = {}) {
  try {
    return await api.book(payload);
  } catch (err) {
    if (!isConnectionError(err)) throw err;
  }
  onRetry?.();
  if (retryPauseMs > 0) await new Promise((r) => setTimeout(r, retryPauseMs));
  try {
    return await api.book(payload);
  } catch (err) {
    if (!isConnectionError(err)) throw err;
    throw Object.assign(new Error(BOOK_RETRY_FAILED_TEXT), { code: err.code, retried: true });
  }
}

// Тело POST /api/book из снимка поиска, карточки и полей формы.
// expectedTotal — цена, которую видел гость: сервер сверит её со свежей и ответит 409 при расхождении.
// formToken — из ответа /payment-options, okb_extra — приманка для ботов (у людей пустая), T10d.
export function buildBookPayload({ search, card, values, paymentMethodID, formToken }) {
  return {
    formToken: formToken ?? null,
    okb_extra: String(values.okb_extra ?? ""),
    from: search.checkin,
    to: search.checkout,
    catId: card.catId,
    tariffId: card.tariffId,
    paymentMethodID,
    adults: search.adults,
    children: search.children ?? 0,
    expectedTotal: card.total,
    guest: {
      firstName: String(values.firstName ?? "").trim(),
      lastName: String(values.lastName ?? "").trim(),
      middleName: String(values.middleName ?? "").trim(),
      phone: normalizePhone(values.phone),
      email: String(values.email ?? "").trim(),
    },
    comment: String(values.comment ?? "").trim(),
    agree: values.agree === true,
    // доп. услуги (T11): серверу — только id и количество; сумма — то, что видел гость
    services: (values.services ?? []).map(({ id, count }) => ({ id, count })),
    expectedServicesTotal: servicesTotal(values.services ?? []),
  };
}

// Строка согласия (T9.5h): в тексте Шелтера делаем ссылками фразы про правила бронирования
// (agreeUrl) и обработку персональных данных (pdUrl) — как в родном модуле. Фразы нет — ссылка
// добавляется в конец отдельными словами. esc — экранирование HTML (передаётся, lib без UI).
const AGREE_LINKS = [
  { key: "agreeUrl", re: /правил[а-яё]*\s+(?:онлайн-)?бронирования/iu, tail: "Правила бронирования" },
  {
    key: "pdUrl",
    re: /обработк[а-яё]*\s+персональных\s+данных/iu,
    tail: "Обработка персональных данных",
  },
];
export function agreeHtml(text, links, esc) {
  let html = esc(String(text ?? ""));
  const tails = [];
  for (const { key, re, tail } of AGREE_LINKS) {
    const url = links?.[key];
    if (!url) continue;
    const a = (label) => `<a href="${esc(url)}" target="_blank" rel="noopener">${label}</a>`;
    if (re.test(html)) html = html.replace(re, (m) => a(m));
    else tails.push(a(tail));
  }
  return tails.length ? `${html} ${tails.join(" · ")}` : html;
}

// «Остаток при заселении»: total − предоплата + доп. услуги (они оплачиваются на месте, T11);
// без предоплаты остатка нет (null — строка скрывается)
export function restAtCheckin(total, prepaymentRub, servicesTotalRub = 0) {
  if (total == null || prepaymentRub == null) return null;
  return Math.max(0, total - prepaymentRub) + (Number(servicesTotalRub) || 0);
}

// Возврат с оплаты: ищем orderToken в query. Формат редиректа Шелтера неизвестен (открытый
// вопрос ROADMAP) — пробуем наш параметр и вероятные имена Шелтера.
export function statusParamsFromLocation(searchString) {
  let params;
  try {
    params = new URLSearchParams(searchString ?? "");
  } catch {
    return null;
  }
  for (const key of ["okb_order", "orderToken", "orderID", "orderId"]) {
    const value = (params.get(key) ?? "").trim();
    if (value && value.length <= 100) return { orderToken: value };
  }
  return null;
}
